import { motion } from "framer-motion";
import type { Symptom } from "@/components/site/SymptomGrid";

const POPPINS = { fontFamily: "'Poppins', sans-serif" } as const;

function Item({ item, align, index }: { item: Symptom; align: "left" | "right"; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      className={`relative rounded-2xl border border-border/60 bg-background p-6 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand md:p-7 ${
        align === "right" ? "lg:text-left" : "lg:text-right"
      }`}
    >
      <span
        className="text-xs font-semibold tabular-nums tracking-wider text-[#693D94]"
        style={POPPINS}
      >
        {String(index + 1).padStart(2, "0")}
      </span>
      <h3 className="mt-2 text-lg font-bold leading-snug text-[#111111] md:text-xl" style={POPPINS}>
        {item.title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{item.body}</p>
    </motion.article>
  );
}

/**
 * Four symptoms arranged around a giant purple exclamation point on desktop.
 * On mobile it collapses to a single scroll-friendly column with a purple rail.
 */
export function SymptomRadial({ items }: { items: readonly Symptom[] }) {
  const left = items.filter((_, i) => i % 2 === 0);
  const right = items.filter((_, i) => i % 2 === 1);

  return (
    <div className="relative">
      {/* Desktop / tablet: items around the mark */}
      <div className="hidden lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-x-10 xl:gap-x-14">
        <div className="space-y-8">
          {left.map((item, i) => (
            <Item key={item.title} item={item} align="left" index={i * 2} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative flex w-[190px] justify-center xl:w-[230px]"
          aria-hidden="true"
        >
          <div
            className="pointer-events-none absolute inset-0 rounded-full opacity-60 blur-3xl"
            style={{
              background:
                "radial-gradient(closest-side, color-mix(in oklab, var(--primary-glow) 45%, transparent), transparent)",
            }}
          />
          <span
            className="relative select-none text-[15rem] font-black leading-none text-[#693D94] xl:text-[19rem]"
            style={POPPINS}
          >
            !
          </span>
        </motion.div>

        <div className="space-y-8">
          {right.map((item, i) => (
            <Item key={item.title} item={item} align="right" index={i * 2 + 1} />
          ))}
        </div>
      </div>

      {/* Mobile: purple rail + stacked cards */}
      <div className="lg:hidden">
        <div className="relative pl-10 sm:pl-12">
          <span
            className="absolute left-[13px] top-2 bottom-2 w-px bg-primary/25 sm:left-[17px]"
            aria-hidden="true"
          />
          <span
            className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#693D94] text-base font-black leading-none text-white sm:h-9 sm:w-9 sm:text-xl"
            style={POPPINS}
            aria-hidden="true"
          >
            !
          </span>
          <div className="space-y-5 pt-1">
            {items.map((item, i) => (
              <Item key={item.title} item={item} align="right" index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
